import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useSnackbar } from 'notistack';
import {
  Link,
  makeStyles,
  IconButton,
} from '@material-ui/core';
import { get } from 'lodash';
import { service } from '@src/services/exception-service';
import ExceptionDetailDialog from '@src/components/dialog/exception-detail';
import { CloseIcon } from 'shared-library/dist/components/material-icon';
import moment from 'moment';
import NotificationItem from './item';
import { ERROR_TYPE_API, ERROR_TYPE_APPLICATION, ERROR_TYPE_NETWORK } from '../../constants';

const useStyles = makeStyles((theme) => ({
  fontWeightBold: {
    fontWeight: 'bold',
  },
  closeIcon: {
    color: theme.palette.common.gray,
  },
  actionRoot: {
    display: 'flex',
    alignItems: 'center',
  },
  errorMessage: {
    color: theme.palette.error[theme.palette.type],
  },
  warningMessage: {
    color: theme.palette.warning[theme.palette.type],
  },
}));

const Notification = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [errorInfo, setErrorInfo] = useState({});
  const styles = useStyles();

  const handleClose = (id) => () => {
    closeSnackbar(id);
  };

  const openExceptionDetailDialog = (ei) => () => {
    setErrorInfo(ei);
    setOpen(true);
  };

  const closeExceptionDetailDialog = () => {
    setOpen(false);
  };

  const generateApiException = (exceptionList, {
    type, exceptionDetail, informationList, category,
  }) => {
    const queryString = JSON.parse(exceptionDetail.config.data).query;
    const errorTitle = queryString.match(/[query,mutation].*?{[\\n\s]*(.+?)[\\n\s]*[(,{]/i)[1];
    const counter = get(exceptionList, ['0', 'id'], 0);
    return {
      id: counter + 1,
      category,
      title: `${category}: ${errorTitle} ${type}`,
      message: informationList.message,
      time: moment(),
      read: false,
      detail: {
        informationList,
        exceptionDetail,
      },
    };
  };

  const generateAppException = (exceptionList, {
    informationList, category,
  }) => {
    const counter = get(exceptionList, ['0', 'id'], 0);
    return {
      id: counter + 1,
      category,
      title: informationList.title,
      message: informationList.message,
      time: moment(),
      read: false,
      detail: {
      },
    };
  };

  const generateNetworkException = (exceptionList, {
    informationList, category,
  }) => {
    const counter = get(exceptionList, ['0', 'id'], 0);
    return {
      id: counter + 1,
      category,
      title: 'Error: Network',
      message: informationList.message,
      time: moment(),
      read: false,
      detail: {
      },
    };
  };

  const generateException = (exceptionList, ei) => {
    const { type } = ei;
    switch (type) {
      case ERROR_TYPE_APPLICATION:
        return generateAppException(exceptionList, ei);
      case ERROR_TYPE_API:
        return generateApiException(exceptionList, ei);
      case ERROR_TYPE_NETWORK:
        return generateNetworkException(exceptionList, ei);
      default:
        return generateAppException(exceptionList, ei);
    }
  };

  service.enqueue = (ei) => {
    const {
      exceptionDetail, informationList, category,
    } = ei;
    const exceptionList = JSON.parse(sessionStorage.getItem('exceptionList')) || [];
    const newException = generateException(exceptionList, ei);
    try {
      sessionStorage.setItem('exceptionList', JSON.stringify([
        newException,
        ...exceptionList,
      ]));
    } catch (ex) {
      console.error(ex);
    }

    const Action = ({ id, className, variant }) => (
      <span className={className}>
        <div className={styles.actionRoot}>
          <Link
            className={classnames(styles.fontWeightBold, styles[`${variant}Message`])}
            onClick={openExceptionDetailDialog(ei)}
          >
            More
          </Link>
          <IconButton
            aria-label="close"
            onClick={handleClose(id)}
          >
            <CloseIcon className={styles.closeIcon} />
          </IconButton>
        </div>
      </span>
    );
    Action.propTypes = {
      className: PropTypes.string.isRequired,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      variant: PropTypes.string.isRequired,
    };

    enqueueSnackbar(
      informationList.message,
      {
        content: (key, message) => (
          <NotificationItem
            id={key}
            message={message}
            variant={category}
            action={(category === 'error' && exceptionDetail) ? Action : undefined}
          />
        ),
        preventDuplicate: true,
      },
    );
  };

  return (
    <ExceptionDetailDialog
      open={open}
      onClose={closeExceptionDetailDialog}
      errorInfo={errorInfo}
    />
  );
};

export default Notification;
export { NotificationItem };
